// ============================================================
// ANOMALY DETECTOR — Fase 7.7
// Detecta desvios estatísticos no contexto operacional
// Usa Z-score simples contra thresholds históricos médios
// ============================================================
import type { RiskContext } from '@/types/operational'
import type { AnomalyEvent } from '@/types/ai'

// ─── Expected ranges (heuristic baselines) ───────────────────
const BASELINES: Record<string, { mean: number; stddev: number }> = {
  efetivo_pct:        { mean: 90, stddev: 10 },   // % of meta
  avanco_desvio:      { mean: 0,  stddev: 5  },   // realizado - previsto
  temperatura:        { mean: 26, stddev: 6  },   // °C
  precipitacao_mm:    { mean: 2,  stddev: 8  },
  vento_kmh:          { mean: 15, stddev: 12 },
  aprs_vencidas_pct:  { mean: 5,  stddev: 8  },   // % of total
  ocorrencias_criticas: { mean: 0, stddev: 1 },
  supply_criticos:    { mean: 0, stddev: 1 },
}

function zScore(value: number, mean: number, stddev: number): number {
  if (stddev === 0) return 0
  return Math.abs(value - mean) / stddev
}

function severity(z: number): 'info' | 'warning' | 'critical' {
  if (z >= 3) return 'critical'
  if (z >= 2) return 'warning'
  return 'info'
}

export function detectAnomalies(ctx: RiskContext): AnomalyEvent[] {
  const anomalies: AnomalyEvent[] = []

  // ── Efetivo ─────────────────────────────────────────────
  if (ctx.producao.efetivoMeta > 0) {
    const efPct  = (ctx.producao.efetivoPresente / ctx.producao.efetivoMeta) * 100
    const bl     = BASELINES.efetivo_pct
    const z      = zScore(efPct, bl.mean, bl.stddev)
    if (z >= 2 && efPct < bl.mean) {
      anomalies.push({
        field:       'efetivo_pct',
        expected:    bl.mean,
        actual:      efPct,
        deviation:   ((efPct - bl.mean) / bl.mean) * 100,
        severity:    severity(z),
        description: `Efetivo ${efPct.toFixed(0)}% (esperado ~${bl.mean}%)`,
      })
    }
  }

  // ── Avanço desvio ─────────────────────────────────────
  if (ctx.producao.avancoPrevisto > 0) {
    const desvio = ctx.producao.avancoRealizado - ctx.producao.avancoPrevisto
    const bl     = BASELINES.avanco_desvio
    const z      = zScore(desvio, bl.mean, bl.stddev)
    if (z >= 2 && desvio < 0) {
      anomalies.push({
        field:       'avanco_desvio',
        expected:    0,
        actual:      desvio,
        deviation:   desvio,
        severity:    severity(z),
        description: `Avanço ${Math.abs(desvio).toFixed(1)}pp abaixo do planejado`,
      })
    }
  }

  // ── Clima ────────────────────────────────────────────
  if (ctx.clima) {
    const { precipitacaoMm, vento, temperatura } = ctx.clima

    const blTemp = BASELINES.temperatura
    const zTemp  = zScore(temperatura, blTemp.mean, blTemp.stddev)
    if (zTemp >= 2) {
      anomalies.push({
        field:       'temperatura',
        expected:    blTemp.mean,
        actual:      temperatura,
        deviation:   ((temperatura - blTemp.mean) / blTemp.mean) * 100,
        severity:    severity(zTemp),
        description: `Temperatura ${temperatura}°C (usual ~${blTemp.mean}°C)`,
      })
    }

    const blVento = BASELINES.vento_kmh
    const zVento  = zScore(vento, blVento.mean, blVento.stddev)
    if (zVento >= 2 && vento > blVento.mean) {
      anomalies.push({
        field:       'vento_kmh',
        expected:    blVento.mean,
        actual:      vento,
        deviation:   ((vento - blVento.mean) / blVento.mean) * 100,
        severity:    severity(zVento),
        description: `Vento ${vento}km/h (usual ~${blVento.mean}km/h)`,
      })
    }

    const blChuva = BASELINES.precipitacao_mm
    const zChuva  = zScore(precipitacaoMm, blChuva.mean, blChuva.stddev)
    if (zChuva >= 1.5 && precipitacaoMm > blChuva.mean) {
      anomalies.push({
        field:       'precipitacao_mm',
        expected:    blChuva.mean,
        actual:      precipitacaoMm,
        deviation:   ((precipitacaoMm - blChuva.mean) / (blChuva.mean || 1)) * 100,
        severity:    severity(zChuva),
        description: `Precipitação ${precipitacaoMm}mm (usual ~${blChuva.mean}mm)`,
      })
    }
  }

  // ── SST ──────────────────────────────────────────────
  if (ctx.sst.aprsTotal > 0) {
    const vencPct = (ctx.sst.aprsVencidas / ctx.sst.aprsTotal) * 100
    const bl      = BASELINES.aprs_vencidas_pct
    const z       = zScore(vencPct, bl.mean, bl.stddev)
    if (z >= 2) {
      anomalies.push({
        field:       'aprs_vencidas_pct',
        expected:    bl.mean,
        actual:      vencPct,
        deviation:   vencPct - bl.mean,
        severity:    severity(z),
        description: `${vencPct.toFixed(0)}% APRs vencidas (usual <${bl.mean}%)`,
      })
    }
  }

  if (ctx.sst.ocorrenciasCriticas > 0) {
    const bl = BASELINES.ocorrencias_criticas
    const z  = zScore(ctx.sst.ocorrenciasCriticas, bl.mean, bl.stddev)
    anomalies.push({
      field:       'ocorrencias_criticas',
      expected:    0,
      actual:      ctx.sst.ocorrenciasCriticas,
      deviation:   ctx.sst.ocorrenciasCriticas * 100,
      severity:    'critical',
      description: `${ctx.sst.ocorrenciasCriticas} ocorrência(s) crítica(s) ativa(s)`,
    })
  }

  return anomalies
}
